import type { Breakpoint, SxProps, Theme } from '@mui/material';
import {
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs as MuiTabs,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { ReactNode } from 'react';

import { Text } from '../Text/Text';

interface Props<T extends string | number> {
  tabs: {
    label: string;
    value: T;
    icon?: ReactNode;
    tooltip?: string;
    id?: string;
    groupPosition?: 'start' | 'middle' | 'end';
  }[];
  value: T;
  onChange: (value: T) => void;
  textColor?: 'primary' | 'secondary' | 'inherit';
  indicatorColor?: 'primary' | 'secondary';
  sx?: SxProps<Theme>;
  compactBelow?: Breakpoint;
  ariaLabel?: string;
}

export function Tabs<T extends string | number>({
  tabs,
  value,
  onChange,
  textColor = 'primary',
  indicatorColor = 'secondary',
  sx,
  compactBelow,
  ariaLabel,
}: Props<T>) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down(compactBelow ?? 'xs'));
  if (compactBelow && compact) {
    return (
      <Select
        fullWidth
        size="small"
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
        inputProps={{ 'aria-label': ariaLabel }}
        sx={{ minHeight: '2.75rem' }}
      >
        {tabs.map((tab) => (
          <MenuItem key={tab.value} value={tab.value} sx={{ whiteSpace: 'normal' }}>
            {tab.label}
          </MenuItem>
        ))}
      </Select>
    );
  }
  return (
    <MuiTabs
      value={value}
      textColor={textColor}
      indicatorColor={indicatorColor}
      variant="scrollable"
      scrollButtons="auto"
      sx={sx}
    >
      {tabs.map((tab) => {
        const button = (
          <Tab
            id={tab.id}
            data-group-position={tab.groupPosition}
            key={tab.value}
            label={
              tab.icon ? (
                <Stack direction="row" alignItems="center" gap="0.5rem">
                  {tab.icon} <Text bold={value === tab.value}>{tab.label}</Text>
                </Stack>
              ) : (
                tab.label
              )
            }
            value={tab.value}
            onClick={() => onChange(tab.value)}
            sx={{
              textTransform: 'none',
              '&.Mui-selected': {
                fontWeight: 'bold',
              },
              padding: '0 0.5rem',
            }}
          />
        );
        return tab.tooltip ? (
          <Tooltip
            describeChild
            key={tab.value}
            title={tab.tooltip}
            // MuiTabs reads the value from its direct children to track selection.
            {...{ value: tab.value }}
          >
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </MuiTabs>
  );
}
